import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth";
import Logo from "@/components/Logo";
import LangToggle from "@/components/LangToggle";
import { LangStatsTable } from "@/components/LangStatsTable";
import {
  ArrowLeft,
  Users,
  Mail,
  CreditCard,
  Database,
  Activity,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock,
  Megaphone,
  Send,
  Trash2,
  Edit3,
  Filter,
  Tag,
  Plus,
  Siren,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="glass-card rounded-md p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon className="w-5 h-5 text-gold" strokeWidth={1.4} />
        <span className="font-label text-faded">{label}</span>
      </div>
      <div className="font-display text-3xl text-gold-light">{value}</div>
      {accent && <div className="text-xs text-muted mt-1">{accent}</div>}
    </div>
  );
}

function fmtBytes(b) {
  if (b > 1024 * 1024 * 1024) return `${(b / 1073741824).toFixed(2)} GB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

// Weekly revenue breakdown — sum of payment_transactions.amount where
// status=complete, split by source (full-price / WELCOME25 win-back / BETA100
// founders) + 8-week trend for the weekly review.
function RevenueBreakdown({ data, onRefresh }) {
  const fmt = (n) => `€${(Number(n) || 0).toFixed(2)}`;
  const sources = data.by_source;
  const total = data.total;
  const peak = Math.max(...data.weekly_trend.map((w) => w.total), 1);
  return (
    <section data-testid="admin-revenue-breakdown">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-label text-gold">Revenue por fuente</h2>
        <button
          onClick={onRefresh}
          className="text-xs text-faded hover:text-gold-light flex items-center gap-1"
          data-testid="admin-revenue-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Recargar
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="glass-card rounded-md p-5" data-testid="rev-total">
          <div className="flex items-center justify-between mb-3">
            <CreditCard className="w-5 h-5 text-gold" strokeWidth={1.4} />
            <span className="font-label text-faded">Total cobrado</span>
          </div>
          <div className="font-display text-3xl text-gold-light">{fmt(total.amount_eur)}</div>
          <div className="text-xs text-muted mt-1">{total.count} pagos completados</div>
        </div>

        <div className="glass-card rounded-md p-5" data-testid="rev-full-price">
          <div className="flex items-center justify-between mb-3">
            <CreditCard className="w-5 h-5 text-emerald-400" strokeWidth={1.4} />
            <span className="font-label text-faded">Pago completo</span>
          </div>
          <div className="font-display text-3xl text-emerald-300">{fmt(sources.full_price.amount_eur)}</div>
          <div className="text-xs text-muted mt-1">{sources.full_price.count} ventas</div>
        </div>

        <div className="glass-card rounded-md p-5" data-testid="rev-welcome25">
          <div className="flex items-center justify-between mb-3">
            <CreditCard className="w-5 h-5 text-amber-400" strokeWidth={1.4} />
            <span className="font-label text-faded">Win-back -25%</span>
          </div>
          <div className="font-display text-3xl text-amber-300">{fmt(sources.welcome25.amount_eur)}</div>
          <div className="text-xs text-muted mt-1">
            {sources.welcome25.count} canjes · {data.funnel.welcome25_codes_issued} emitidos · {data.funnel.conversion_pct}% conv.
          </div>
        </div>

        <div className="glass-card rounded-md p-5" data-testid="rev-beta100">
          <div className="flex items-center justify-between mb-3">
            <CreditCard className="w-5 h-5 text-purple-400" strokeWidth={1.4} />
            <span className="font-label text-faded">BETA100 (founders)</span>
          </div>
          <div className="font-display text-3xl text-purple-300">{sources.beta100.count}</div>
          <div className="text-xs text-muted mt-1">canjes · 0€ (regalo)</div>
        </div>
      </div>

      {sources.other_discount.count > 0 && (
        <div className="text-xs text-faded mb-3" data-testid="rev-other">
          Otros descuentos: {fmt(sources.other_discount.amount_eur)} · {sources.other_discount.count} pagos
        </div>
      )}

      <div className="glass-card rounded-md p-5">
        <h3 className="font-label text-gold mb-3">Tendencia 8 semanas</h3>
        <div className="grid grid-cols-8 gap-2 items-end h-32" data-testid="rev-weekly-bars">
          {data.weekly_trend.map((w) => {
            const h = Math.round((w.total / peak) * 100);
            const isCurrent = w === data.weekly_trend[data.weekly_trend.length - 1];
            return (
              <div key={w.week_start} className="flex flex-col items-center gap-1">
                <div
                  className={`w-full rounded-sm transition-all ${isCurrent ? "bg-gold" : "bg-gold-subtle"}`}
                  style={{ height: `${Math.max(h, 2)}%`, minHeight: "2px" }}
                  title={`Semana ${w.week_start}: ${fmt(w.total)} (win-back ${fmt(w.welcome25)})`}
                />
                <div className="text-[10px] text-faded">{w.week_start.slice(5)}</div>
                <div className="text-[10px] text-muted">{fmt(w.total)}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-faded mt-3">
          Generado el {new Date(data.generated_at).toLocaleString()}
        </p>
      </div>
    </section>
  );
}

// Last 10 client-side crashes grouped by message — fed by the React
// ErrorBoundary's sendBeacon. Pairs with the revenue counter for the
// weekly review.
function CrashesCard({ data, onRefresh }) {
  const groups = data?.groups || [];
  const total24h = data?.total_24h || 0;
  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleString(); }
    catch { return iso; }
  };
  const trimUa = (ua) => {
    if (!ua) return "";
    // Keep the browser + OS, drop the long Mozilla preamble
    const m = ua.match(/(Chrome|Firefox|Safari|Edge|OPR)\/[\d.]+/i);
    const os = ua.match(/(Mac OS X [\d_]+|Windows NT [\d.]+|Linux|Android \d+|iPhone OS [\d_]+|iPad)/);
    return [m?.[0], os?.[0]].filter(Boolean).join(" · ");
  };
  return (
    <section data-testid="admin-crashes-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-label text-gold">Últimos crashes</h2>
        <div className="flex items-center gap-3 text-xs">
          <span
            className={total24h > 0 ? "text-red-300" : "text-faded"}
            data-testid="crashes-total-24h"
          >
            {total24h} en 24h
          </span>
          <button
            onClick={onRefresh}
            className="text-faded hover:text-gold-light flex items-center gap-1"
            data-testid="admin-crashes-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Recargar
          </button>
        </div>
      </div>

      <div className="glass-card rounded-md p-5">
        {groups.length === 0 ? (
          <p className="text-faded text-sm" data-testid="crashes-empty">
            Ningún crash registrado. La app va estable.
          </p>
        ) : (
          <ul className="divide-y" style={{ borderColor: "rgba(201,163,106,0.15)" }}>
            {groups.map((g, i) => (
              <li key={`${g.message}-${i}`} className="py-3" data-testid={`crash-row-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-mono text-sm text-red-300 truncate"
                      title={g.message}
                      data-testid={`crash-message-${i}`}
                    >
                      {g.message || "(empty message)"}
                    </p>
                    <p
                      className="text-[11px] text-faded truncate"
                      title={g.sample_url}
                    >
                      {g.sample_url || "—"}
                    </p>
                    <p className="text-[11px] text-muted">
                      {trimUa(g.sample_ua)} · {fmtTime(g.last_seen)}
                    </p>
                  </div>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
                    style={{
                      background: "rgba(239,68,68,0.12)",
                      color: "#F87171",
                      border: "1px solid rgba(239,68,68,0.3)",
                    }}
                    data-testid={`crash-count-${i}`}
                  >
                    ×{g.count}
                  </span>
                </div>
                {g.sample_stack && (
                  <details className="mt-1">
                    <summary className="text-[11px] text-faded cursor-pointer hover:text-gold-light">
                      Stack
                    </summary>
                    <pre
                      className="text-[10px] mt-1 p-2 rounded overflow-auto max-h-32"
                      style={{ background: "rgba(0,0,0,0.4)", color: "#FCA5A5" }}
                    >
                      {g.sample_stack}
                    </pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}





export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [stats, setStats] = useState(null);
  const [langStats, setLangStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [crashes, setCrashes] = useState(null);
  const [users, setUsers] = useState({ total: 0, items: [] });
  const [emails, setEmails] = useState([]);
  const [emailFilter, setEmailFilter] = useState("");
  const [search, setSearch] = useState("");
  const [testOnly, setTestOnly] = useState(false);
  const [tab, setTab] = useState("overview");
  // Broadcast
  const [bcSubject, setBcSubject] = useState("");
  const [bcBody, setBcBody] = useState("");
  const [bcSending, setBcSending] = useState(false);
  const [bcResult, setBcResult] = useState(null);
  const [bcPreview, setBcPreview] = useState(null);
  const [bcConfirm, setBcConfirm] = useState(false); // two-step confirm gate
  const [bcTestSending, setBcTestSending] = useState(false);
  const [bcTestResult, setBcTestResult] = useState(null);
  // User editing / bulk
  const [editUser, setEditUser] = useState(null);
  const [emailUser, setEmailUser] = useState(null);
  const [bulkPattern, setBulkPattern] = useState("@example.com");
  const [busyUser, setBusyUser] = useState(null);
  // Discount codes
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", percent: 10, max_uses: "", expires_at: "", applies_to: "all" });
  const [creatingCode, setCreatingCode] = useState(false);

  const loadCodes = async () => {
    try {
      const r = await api.get("/admin/discount-codes");
      setCodes(r.data || []);
    } catch {
      setCodes([]);
    }
  };

  const createCode = async () => {
    if (!newCode.code.trim()) return toast.error("Indica un código");
    setCreatingCode(true);
    try {
      const payload = {
        code: newCode.code.trim().toUpperCase(),
        percent: Number(newCode.percent),
        applies_to: newCode.applies_to,
      };
      if (newCode.max_uses) payload.max_uses = Number(newCode.max_uses);
      if (newCode.expires_at) payload.expires_at = new Date(newCode.expires_at).toISOString();
      await api.post("/admin/discount-codes", payload);
      toast.success(`Código ${payload.code} creado`);
      setNewCode({ code: "", percent: 10, max_uses: "", expires_at: "", applies_to: "all" });
      loadCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear el código");
    } finally {
      setCreatingCode(false);
    }
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`¿Borrar el código ${code}?`)) return;
    try {
      await api.delete(`/admin/discount-codes/${code}`);
      toast.success("Código eliminado");
      loadCodes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`¿Borrar definitivamente a ${u.email}? Esto elimina TODO su contenido.`)) return;
    setBusyUser(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`Usuario ${u.email} eliminado`);
      loadUsers(search, testOnly);
      if (tab === "overview") loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    } finally {
      setBusyUser(null);
    }
  };

  const saveUserEdit = async () => {
    if (!editUser) return;
    try {
      const payload = {
        plan: editUser.plan,
        email_verified: editUser.email_verified,
        name: editUser.name,
      };
      if (editUser.pol_interval_days_input !== undefined && editUser.pol_interval_days_input !== "") {
        payload.pol_interval_days = Number(editUser.pol_interval_days_input);
      }
      await api.put(`/admin/users/${editUser.user_id}`, payload);
      toast.success("Usuario actualizado");
      setEditUser(null);
      loadUsers(search, testOnly);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const bulkDelete = async () => {
    if (!bulkPattern.trim()) return toast.error("Indica un patrón de email");
    if (!window.confirm(`¿Borrar TODOS los usuarios cuyo email contenga "${bulkPattern}"? Acción irreversible.`)) return;
    try {
      const r = await api.post("/admin/users/bulk-delete", { email_pattern: bulkPattern });
      toast.success(`${r.data.deleted}/${r.data.requested} usuarios eliminados`);
      loadUsers(search, testOnly);
      if (tab === "overview") loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en bulk delete");
    }
  };

  const triggerPolAlert = async (u) => {
    if (!window.confirm(`¿Forzar alerta de Fe de Vida para ${u.email}?\n\nSe enviarán emails REALES a TODOS sus contactos de confianza como si el plazo hubiese expirado.`)) return;
    setBusyUser(u.user_id);
    try {
      const r = await api.post(`/admin/users/${u.user_id}/pol/trigger`);
      toast.success(`Alerta enviada a ${r.data.contacts_alerted || 0} contactos`);
      loadUsers(search, testOnly);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo disparar");
    } finally {
      setBusyUser(null);
    }
  };

  const deleteEmail = async (e) => {
    if (!e.provider_id) return toast.error("Sin provider_id — usa limpieza por filtro");
    if (!window.confirm(`¿Borrar este email del histórico?`)) return;
    try {
      await api.delete(`/admin/emails/${encodeURIComponent(e.provider_id)}`);
      toast.success("Email eliminado");
      loadEmails(emailFilter);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Error al borrar");
    }
  };

  const bulkDeleteEmails = async (mode) => {
    let params;
    let label;
    if (mode === "all") {
      if (!window.confirm("¿Borrar TODO el histórico de emails? Acción irreversible.")) return;
      params = { all: true };
      label = "todos";
    } else if (mode === "old") {
      params = { older_than_days: 30 };
      label = "más de 30 días";
    } else {
      params = { status: mode };
      label = mode;
    }
    if (mode !== "all" && !window.confirm(`¿Borrar emails (${label})?`)) return;
    try {
      const r = await api.delete("/admin/emails", { params });
      toast.success(`${r.data.deleted} emails eliminados`);
      loadEmails(emailFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar");
    }
  };

  // Verify admin access
  useEffect(() => {
    api.get("/admin/me")
      .then(() => setAuthorized(true))
      .catch(() => {
        setAuthorized(false);
        toast.error("Acceso restringido — inicia sesión como admin");
        setTimeout(() => navigate("/admin-login"), 1000);
      });
  }, [navigate]);

  // Load data when tab changes
  const loadStats = () =>
    api.get("/admin/stats")
      .then((r) => { setStats(r.data); setStatsError(null); })
      .catch((e) => {
        setStatsError(e?.response?.data?.detail || e?.message || "Error cargando estadísticas");
      });
  const loadRevenue = () =>
    api.get("/admin/revenue")
      .then((r) => setRevenue(r.data))
      .catch(() => setRevenue(null));
  const loadCrashes = () =>
    api.get("/admin/crashes?limit=10")
      .then((r) => setCrashes(r.data))
      .catch(() => setCrashes(null));
  const loadLangStats = () =>
    api.get("/admin/stats/lang")
      .then((r) => setLangStats(r.data))
      .catch((e) => {
        setLangStats(null);
        toast.error(
          e?.response?.data?.detail ||
            e?.message ||
            "No se pudo cargar las métricas por idioma"
        );
      });
  const loadUsers = (q = "", test_only = false) =>
    api.get("/admin/users", { params: { limit: 100, q, test_only } }).then((r) => setUsers(r.data));
  const loadEmails = (status = "") =>
    api.get("/admin/emails", { params: { limit: 100, status: status || undefined } }).then((r) => setEmails(r.data));

  useEffect(() => {
    if (!authorized) return;
    if (tab === "overview") { loadStats(); loadLangStats(); loadRevenue(); loadCrashes(); }
    if (tab === "users") loadUsers(search, testOnly);
    if (tab === "emails") loadEmails(emailFilter);
    if (tab === "broadcast") {
      api.get("/admin/broadcast/preview").then((r) => setBcPreview(r.data)).catch(() => {});
    }
    if (tab === "discounts") loadCodes();
  // eslint-disable-next-line
  }, [authorized, tab, emailFilter, testOnly]);

  if (authorized === null) {
    return (
      <div className="min-h-screen-safe bg-obsidian flex items-center justify-center text-muted">
        <Activity className="w-5 h-5 animate-spin mr-2" /> Verificando acceso…
      </div>
    );
  }
  if (authorized === false) return null;

  return (
    <div className="min-h-screen-safe bg-obsidian text-white">
      <header className="glass-header pt-safe-or-3">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="text-muted hover:text-gold-light flex items-center gap-2 text-sm">
              <ArrowLeft className="w-4 h-4" /> Volver
            </button>
            <div className="hidden sm:flex items-center gap-2">
              <Logo />
              <span className="text-[10px] uppercase tracking-widest text-gold bg-gold/10 border border-gold-subtle px-2 py-0.5 rounded">Admin</span>
            </div>
          </div>
          <LangToggle />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-display text-4xl md:text-5xl mb-2">Panel de Administración</h1>
        <p className="text-muted mb-8 text-sm">Conectado como <span className="text-gold-light">{user?.email}</span></p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gold-subtle overflow-x-auto">
          {[
            { k: "overview", label: "Resumen", icon: Activity },
            { k: "users", label: "Usuarios", icon: Users },
            { k: "emails", label: "Emails", icon: Mail },
            { k: "broadcast", label: "Comunicado", icon: Megaphone },
            { k: "discounts", label: "Códigos", icon: Tag },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`admin-tab-${t.k}`}
                className={`shrink-0 flex items-center gap-2 px-4 py-3 text-sm transition border-b-2 -mb-px ${
                  tab === t.k ? "text-gold border-gold" : "text-faded border-transparent hover:text-muted"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div className="space-y-8">
            {statsError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 text-sm text-red-300">
                ⚠️ {statsError}
                <button onClick={loadStats} className="ml-3 underline text-red-200">Reintentar</button>
              </div>
            )}
            {!stats && !statsError && (
              <div className="text-muted text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 animate-spin" /> Cargando estadísticas…
              </div>
            )}
            {stats && (
              <>
                <div className="flex items-center gap-3">
                  <button onClick={loadStats} className="btn-ghost text-xs">
                    <RefreshCw className="w-3 h-3" /> Actualizar
                  </button>
                  {stats.system?.email_mock_mode && !stats.system?.resend_configured && (
                    <span className="text-[11px] uppercase tracking-widest bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 px-2 py-1 rounded">
                      ⚠️ Modo email simulado (sin Resend)
                    </span>
                  )}
                </div>

                <section>
                  <h2 className="font-label text-gold mb-3">Usuarios</h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={Users} label="Total" value={stats.users.total} testid="stat-users-total" />
                    <StatCard icon={Users} label="Hoy" value={stats.users.today} testid="stat-users-today" />
                    <StatCard icon={Users} label="Esta semana" value={stats.users.week} testid="stat-users-week" />
                    <StatCard
                      icon={Users}
                      label="De pago"
                      value={(stats.users.by_plan.personal || 0) + (stats.users.by_plan.family || 0) + (stats.users.by_plan.premium || 0)}
                      accent={`Free: ${stats.users.by_plan.free}`}
                      testid="stat-users-paid"
                    />
                  </div>
                </section>

                <section>
                  <h2 className="font-label text-gold mb-3">Ingresos & Pagos</h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                      icon={CreditCard}
                      label="Ingresos totales"
                      value={`€${stats.payments.revenue_eur.toFixed(2)}`}
                      testid="stat-revenue"
                    />
                    <StatCard icon={CheckCircle2} label="Pagos completados" value={stats.payments.paid} testid="stat-paid" />
                    <StatCard icon={Clock} label="Pagos pendientes" value={stats.payments.pending} testid="stat-pending" />
                    <StatCard
                      icon={Database}
                      label="Almacenamiento"
                      value={fmtBytes(stats.storage.used_bytes)}
                      testid="stat-storage"
                    />
                  </div>
                </section>

                {revenue && <RevenueBreakdown data={revenue} onRefresh={loadRevenue} />}

                {crashes && <CrashesCard data={crashes} onRefresh={loadCrashes} />}

                <section>
                  <h2 className="font-label text-gold mb-3">Contenido & Emails</h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={Database} label="Items Cofre" value={stats.content.vault_items} />
                    <StatCard icon={Mail} label="Mensajes" value={stats.content.messages} />
                    <StatCard icon={Users} label="Herederos" value={stats.content.heirs} />
                    <StatCard icon={Database} label="Activos digitales" value={stats.content.digital_assets} />
                    <StatCard icon={Mail} label="Emails enviados" value={stats.emails.sent} accent={`${stats.system.email_mock_mode ? 'modo mock' : 'Resend real'}`} />
                    <StatCard icon={Clock} label="Emails en cola" value={stats.emails.queued} />
                    <StatCard icon={AlertCircle} label="Emails fallidos" value={stats.emails.failed} />
                  </div>
                </section>

                {langStats && <LangStatsTable data={langStats} />}
              </>
            )}
          </div>
        )}

        {/* USERS */}
        {tab === "users" && (
          <div>
            <div className="flex items-center gap-2 mb-4 max-w-md">
              <Search className="w-4 h-4 text-faded" />
              <input
                type="text"
                placeholder="Buscar por email o nombre…"
                className="input-field flex-1"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadUsers(search, testOnly)}
                data-testid="admin-user-search"
              />
              <button onClick={() => loadUsers(search, testOnly)} className="btn-ghost text-xs">
                Buscar
              </button>
            </div>

            {/* Pre-launch cleanup filter */}
            <label
              className="inline-flex items-center gap-2 mb-4 text-xs text-muted cursor-pointer select-none"
              data-testid="admin-test-only-toggle"
            >
              <input
                type="checkbox"
                checked={testOnly}
                onChange={(e) => setTestOnly(e.target.checked)}
                className="accent-gold"
              />
              <span>
                Sólo cuentas de prueba{" "}
                <span className="text-faded">
                  (test/qa/demo/example.com)
                </span>
                {testOnly && users?.total != null && (
                  <span className="ml-2 text-gold">· {users.total} encontradas</span>
                )}
              </span>
            </label>

            {/* Bulk delete bar */}
            <div className="glass-card rounded-md p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-bulk-delete">
              <Filter className="w-4 h-4 text-gold shrink-0" />
              <span className="text-xs text-muted whitespace-nowrap">Limpieza masiva por email:</span>
              <input
                type="text"
                className="input-field flex-1 text-xs"
                placeholder="@example.com o ^ks_qa_"
                value={bulkPattern}
                onChange={(e) => setBulkPattern(e.target.value)}
                data-testid="admin-bulk-pattern"
              />
              <button
                onClick={bulkDelete}
                className="btn-ghost text-xs text-red-300 hover:text-red-200 border-red-500/30 whitespace-nowrap"
                data-testid="admin-bulk-delete-btn"
              >
                <Trash2 className="w-3 h-3" /> Borrar coincidencias
              </button>
            </div>

            <div className="text-xs text-faded mb-3">{users.total} usuarios totales</div>
            <div className="glass-card rounded-md overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-obsidian-2">
                  <tr className="text-left text-faded font-label text-xs">
                    <th className="p-3">Email</th>
                    <th className="p-3">Nombre</th>
                    <th className="p-3">Plan</th>
                    <th className="p-3">Verif.</th>
                    <th className="p-3">Registro</th>
                    <th className="p-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.items.map((u) => (
                    <tr key={u.user_id} className="border-t border-gold-subtle hover:bg-obsidian-2/50" data-testid={`admin-user-${u.user_id}`}>
                      <td className="p-3 text-gold-light truncate max-w-[260px]">{u.email}</td>
                      <td className="p-3">{u.name || "—"}</td>
                      <td className="p-3">
                        <span className="text-[10px] uppercase bg-gold/10 text-gold px-2 py-0.5 rounded">{u.plan}</span>
                      </td>
                      <td className="p-3">
                        {u.email_verified ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-400" />
                        )}
                      </td>
                      <td className="p-3 text-faded text-xs whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditUser({ ...u, pol_interval_days_input: "" })}
                          className="text-gold-light hover:text-gold mr-2"
                          title="Editar"
                          data-testid={`admin-edit-${u.user_id}`}
                        >
                          <Edit3 className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => triggerPolAlert(u)}
                          disabled={busyUser === u.user_id || u.user_id === "admin_console"}
                          className="text-yellow-400 hover:text-yellow-300 mr-2 disabled:opacity-30"
                          title="Forzar alerta Fe de Vida (enviará emails reales a contactos)"
                          data-testid={`admin-trigger-pol-${u.user_id}`}
                        >
                          <Siren className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => setEmailUser({ user_id: u.user_id, email: u.email, name: u.name, subject: "", body: "" })}
                          disabled={u.user_id === "admin_console"}
                          className="text-gold-light hover:text-gold mr-2 disabled:opacity-30"
                          title="Enviar email manual desde support@keepsoul.app"
                          data-testid={`admin-email-${u.user_id}`}
                        >
                          <Mail className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => deleteUser(u)}
                          disabled={busyUser === u.user_id || u.user_id === "admin_console"}
                          className="text-red-400 hover:text-red-300 disabled:opacity-30"
                          title={u.user_id === "admin_console" ? "Admin del sistema" : "Borrar"}
                          data-testid={`admin-delete-${u.user_id}`}
                        >
                          {busyUser === u.user_id ? (
                            <RefreshCw className="w-4 h-4 inline animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 inline" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* EMAILS */}
        {tab === "emails" && (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {[
                { k: "", label: "Todos" },
                { k: "sent", label: "Enviados" },
                { k: "outbox", label: "En cola" },
                { k: "failed", label: "Fallidos" },
              ].map((f) => (
                <button
                  key={f.k}
                  onClick={() => setEmailFilter(f.k)}
                  className={`text-xs px-3 py-1.5 rounded border transition ${
                    emailFilter === f.k ? "bg-gold/10 border-gold text-gold-light" : "border-gold-subtle text-muted hover:text-gold-light"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <div className="flex-1" />
              <button
                onClick={() => bulkDeleteEmails("old")}
                className="btn-ghost text-xs text-red-300 hover:text-red-200 border-red-500/30"
                data-testid="email-purge-old"
              >
                <Trash2 className="w-3 h-3" /> Borrar &gt;30 días
              </button>
              {emailFilter && (
                <button
                  onClick={() => bulkDeleteEmails(emailFilter)}
                  className="btn-ghost text-xs text-red-300 hover:text-red-200 border-red-500/30"
                  data-testid="email-purge-status"
                >
                  <Trash2 className="w-3 h-3" /> Borrar {emailFilter}
                </button>
              )}
              <button
                onClick={() => bulkDeleteEmails("all")}
                className="btn-ghost text-xs text-red-300 hover:text-red-200 border-red-500/30"
                data-testid="email-purge-all"
              >
                <Trash2 className="w-3 h-3" /> Borrar TODO
              </button>
            </div>
            <div className="glass-card rounded-md overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead className="bg-obsidian-2">
                  <tr className="text-left text-faded font-label text-xs">
                    <th className="p-3">Asunto</th>
                    <th className="p-3">Para</th>
                    <th className="p-3">Tipo</th>
                    <th className="p-3">Estado</th>
                    <th className="p-3">Provider</th>
                    <th className="p-3">Fecha</th>
                    <th className="p-3 text-right">Acc.</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((e, i) => (
                    <tr key={i} className="border-t border-gold-subtle hover:bg-obsidian-2/50">
                      <td className="p-3 text-gold-light truncate max-w-xs">{e.subject}</td>
                      <td className="p-3 text-xs">{Array.isArray(e.to) ? e.to.join(", ") : e.to}</td>
                      <td className="p-3 text-xs text-faded">{e.tags?.kind || "—"}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${
                          e.status === "sent" ? "bg-emerald-500/10 text-emerald-400" :
                          e.status === "outbox" ? "bg-yellow-500/10 text-yellow-400" :
                          "bg-red-500/10 text-red-400"
                        }`}>{e.status}</span>
                      </td>
                      <td className="p-3 text-xs text-faded">{e.provider || "—"}</td>
                      <td className="p-3 text-xs text-faded">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => deleteEmail(e)}
                          className="text-red-400 hover:text-red-300"
                          title="Borrar"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {emails.length === 0 && (
                <div className="p-8 text-center text-faded text-sm">Sin emails que mostrar</div>
              )}
            </div>
          </div>
        )}
        {/* BROADCAST */}
        {tab === "broadcast" && (
          <div className="max-w-2xl">
            <div className="glass-card rounded-md p-6 mb-6">
              <div className="flex items-start gap-3 mb-4">
                <Megaphone className="w-5 h-5 text-gold shrink-0 mt-1" />
                <div>
                  <h2 className="font-display text-2xl text-gold-light mb-1">Comunicado masivo</h2>
                  <p className="text-xs text-muted leading-relaxed">
                    {bcPreview ? (
                      <>Se enviará a <strong className="text-gold-light">{bcPreview.total_recipients}</strong> usuarios verificados.
                      {bcPreview.skipped_unverified > 0 && (
                        <span className="text-faded"> ({bcPreview.skipped_unverified} no confirmaron email — se omiten)</span>
                      )}
                      {bcPreview.skipped_opted_out > 0 && (
                        <span className="text-faded"> · {bcPreview.skipped_opted_out} optaron por no recibir comunicados</span>
                      )}
                      </>
                    ) : (
                      <>Cargando destinatarios…</>
                    )}
                    {" Usa "}<code className="text-gold">[nombre]</code> para personalizar.
                    {stats?.system?.email_mock_mode ? (
                      <span className="block mt-2 text-yellow-400">⚠ Modo simulado: los emails NO se envían realmente.</span>
                    ) : (
                      <span className="block mt-2 text-red-300">⚠ Resend LIVE: emails reales · ~4 emails/s.</span>
                    )}
                  </p>
                </div>
              </div>
              <label className="font-label text-faded block mb-2">Asunto</label>
              <input
                type="text"
                className="input-field mb-4"
                placeholder="Asunto del email"
                value={bcSubject}
                onChange={(e) => setBcSubject(e.target.value)}
                data-testid="bc-subject"
              />
              <label className="font-label text-faded block mb-2">Cuerpo</label>
              <textarea
                className="input-field mb-4"
                rows={10}
                placeholder="Hola [nombre],&#10;&#10;Te escribimos para confirmar que..."
                value={bcBody}
                onChange={(e) => setBcBody(e.target.value)}
                data-testid="bc-body"
              />
              <div className="flex gap-2 mb-3">
                <button
                  disabled={bcTestSending || !bcSubject.trim() || !bcBody.trim()}
                  onClick={async () => {
                    setBcTestSending(true);
                    setBcTestResult(null);
                    try {
                      const r = await api.post("/admin/broadcast/test", { subject: bcSubject, body: bcBody });
                      setBcTestResult(r.data);
                      toast.success(`Test enviado a ${r.data.sent_to}`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Error al enviar test");
                    } finally {
                      setBcTestSending(false);
                    }
                  }}
                  className="btn-ghost flex-1 justify-center disabled:opacity-40"
                  data-testid="bc-send-test"
                >
                  {bcTestSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {bcTestSending ? "Enviando…" : `Probarlo conmigo (${bcPreview?.admin_email || "admin"})`}
                </button>
              </div>

              {bcTestResult && (
                <div className="mb-3 glass-card rounded p-3 text-xs">
                  <div className={bcTestResult.ok ? "text-emerald-400" : "text-yellow-400"}>
                    {bcTestResult.ok ? "✓" : "⚠"} {bcTestResult.subject} → {bcTestResult.sent_to}
                  </div>
                  <div className="text-faded mt-1">
                    Proveedor: {bcTestResult.provider}
                    {bcTestResult.provider_id ? ` · id ${bcTestResult.provider_id}` : ""}
                  </div>
                  <div className="text-faded mt-1 break-all">
                    Unsubscribe link: <a href={bcTestResult.unsub_url} target="_blank" rel="noreferrer" className="text-gold underline">{bcTestResult.unsub_url}</a>
                  </div>
                </div>
              )}

              {!bcConfirm ? (
                <button
                  disabled={bcSending || !bcSubject.trim() || !bcBody.trim() || !bcPreview}
                  onClick={() => setBcConfirm(true)}
                  className="btn-gold w-full justify-center disabled:opacity-40"
                  data-testid="bc-send"
                >
                  <Send className="w-4 h-4" />
                  Enviar a {bcPreview?.total_recipients ?? "…"} usuarios
                </button>
              ) : (
                <div
                  className="rounded-md border border-red-500/40 bg-red-500/5 p-4"
                  data-testid="bc-confirm-modal"
                >
                  <div className="font-display text-lg text-red-300 mb-2">
                    ⚠ Estás a punto de enviar a {bcPreview?.total_recipients ?? 0} usuarios reales
                  </div>
                  <p className="text-xs text-muted leading-relaxed mb-4">
                    Los emails se enviarán de inmediato vía Resend. Esta acción
                    no se puede deshacer. Cada email incluye el enlace de baja
                    obligatorio y los datos del responsable.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setBcConfirm(false)}
                      className="btn-ghost flex-1 justify-center"
                      data-testid="bc-confirm-cancel"
                    >
                      Cancelar
                    </button>
                    <button
                      disabled={bcSending}
                      onClick={async () => {
                        setBcSending(true);
                        setBcResult(null);
                        try {
                          const r = await api.post("/admin/broadcast", {
                            subject: bcSubject,
                            body: bcBody,
                            confirm_count: bcPreview?.total_recipients,
                          });
                          setBcResult(r.data);
                          toast.success(`Enviados: ${r.data.sent} / Fallidos: ${r.data.failed}`);
                          setBcConfirm(false);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Error al enviar");
                        } finally {
                          setBcSending(false);
                        }
                      }}
                      className="btn-gold flex-1 justify-center disabled:opacity-40"
                      data-testid="bc-confirm-send"
                    >
                      {bcSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {bcSending ? "Enviando…" : "Sí, enviar ahora"}
                    </button>
                  </div>
                </div>
              )}
              {bcResult && (
                <div className="mt-4 glass-card rounded p-3 text-xs">
                  <div className="text-emerald-400">✓ Enviados: {bcResult.sent}</div>
                  {bcResult.failed > 0 && <div className="text-red-300">✗ Fallidos: {bcResult.failed}</div>}
                </div>
              )}
            </div>
          </div>
        )}
        {/* DISCOUNT CODES */}
        {tab === "discounts" && (
          <div className="max-w-3xl">
            <div className="glass-card rounded-md p-6 mb-6">
              <h3 className="font-display text-xl text-gold-light mb-1 flex items-center gap-2">
                <Plus className="w-4 h-4" /> Crear código de descuento
              </h3>
              <p className="text-xs text-muted mb-4">Crea códigos para campañas o regalos. Si pones 100%, el plan se activa sin pasar por Stripe.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="font-label text-faded block mb-2">Código</label>
                  <input
                    type="text"
                    className="input-field uppercase"
                    placeholder="KEEPSOUL10"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    data-testid="dc-code"
                  />
                </div>
                <div>
                  <label className="font-label text-faded block mb-2">Descuento</label>
                  <select
                    className="input-field"
                    value={newCode.percent}
                    onChange={(e) => setNewCode({ ...newCode, percent: Number(e.target.value) })}
                    data-testid="dc-percent"
                  >
                    <option value={10}>10%</option>
                    <option value={25}>25%</option>
                    <option value={50}>50%</option>
                    <option value={100}>100% (gratis)</option>
                  </select>
                </div>
                <div>
                  <label className="font-label text-faded block mb-2">Usos máximos</label>
                  <input
                    type="number"
                    min="1"
                    className="input-field"
                    placeholder="Vacío = ilimitado"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                    data-testid="dc-uses"
                  />
                </div>
                <div>
                  <label className="font-label text-faded block mb-2">Expira (opcional)</label>
                  <input
                    type="date"
                    className="input-field"
                    value={newCode.expires_at}
                    onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                    data-testid="dc-expires"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-label text-faded block mb-2">Plan al que aplica</label>
                  <select
                    className="input-field"
                    value={newCode.applies_to}
                    onChange={(e) => setNewCode({ ...newCode, applies_to: e.target.value })}
                    data-testid="dc-applies"
                  >
                    <option value="all">Todos los planes</option>
                    <option value="personal">Personal mensual</option>
                    <option value="personal_annual">Personal anual</option>
                    <option value="lifetime">De por vida</option>
                  </select>
                </div>
              </div>
              <button
                onClick={createCode}
                disabled={creatingCode || !newCode.code.trim()}
                className="btn-gold w-full sm:w-auto justify-center"
                data-testid="dc-create-btn"
              >
                {creatingCode ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {creatingCode ? "Creando…" : "Crear código"}
              </button>
            </div>

            <h3 className="font-display text-lg text-gold-light mb-3">{codes.length} códigos</h3>
            <div className="glass-card rounded-md overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-obsidian-2">
                  <tr className="text-left text-faded font-label text-xs">
                    <th className="p-3">Código</th>
                    <th className="p-3">%</th>
                    <th className="p-3">Usos</th>
                    <th className="p-3">Expira</th>
                    <th className="p-3">Plan</th>
                    <th className="p-3">Estado</th>
                    <th className="p-3 text-right">Acc.</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => {
                    const usesLeft = c.max_uses ? `${c.used_count || 0}/${c.max_uses}` : `${c.used_count || 0}/∞`;
                    const stColor = {
                      active: "text-emerald-400",
                      expired: "text-red-400",
                      depleted: "text-yellow-400",
                      inactive: "text-faded",
                    }[c.status] || "text-faded";
                    return (
                      <tr key={c.code} className="border-t border-gold-subtle" data-testid={`dc-row-${c.code}`}>
                        <td className="p-3 text-gold-light font-mono">{c.code}</td>
                        <td className="p-3">{c.percent}%</td>
                        <td className="p-3 text-xs">{usesLeft}</td>
                        <td className="p-3 text-xs text-faded">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</td>
                        <td className="p-3 text-xs text-faded">{c.applies_to}</td>
                        <td className={`p-3 text-xs uppercase ${stColor}`}>{c.status}</td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => deleteCode(c.code)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`dc-delete-${c.code}`}
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {codes.length === 0 && (
                <div className="p-8 text-center text-faded text-sm">Aún no hay códigos creados</div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Edit User Modal */}
      {editUser && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={() => setEditUser(null)}
        >
          <div
            className="glass-card rounded-t-md sm:rounded-md w-full sm:max-w-md p-6 sm:p-7 relative"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.5rem)" }}
            data-testid="admin-edit-modal"
          >
            <h3 className="font-display text-2xl text-gold-light mb-1">Editar usuario</h3>
            <p className="text-xs text-muted mb-5 truncate">{editUser.email}</p>

            <label className="font-label text-faded block mb-2">Nombre</label>
            <input
              type="text"
              className="input-field mb-4"
              value={editUser.name || ""}
              onChange={(ev) => setEditUser({ ...editUser, name: ev.target.value })}
              data-testid="admin-edit-name"
            />

            <label className="font-label text-faded block mb-2">Plan</label>
            <select
              className="input-field mb-4"
              value={editUser.plan}
              onChange={(ev) => setEditUser({ ...editUser, plan: ev.target.value })}
              data-testid="admin-edit-plan"
            >
              <option value="free">Gratuito</option>
              <option value="personal">Personal (mensual)</option>
              <option value="personal_annual">Personal (anual)</option>
              <option value="lifetime">De por Vida</option>
            </select>

            <label className="flex items-center gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={!!editUser.email_verified}
                onChange={(ev) => setEditUser({ ...editUser, email_verified: ev.target.checked })}
                className="accent-yellow-600"
                data-testid="admin-edit-verified"
              />
              <span className="text-sm text-gold-light">Email verificado</span>
            </label>

            <label className="font-label text-faded block mb-2">Intervalo Fe de Vida (días)</label>
            <input
              type="number"
              min="7"
              max="365"
              className="input-field mb-1"
              placeholder={editUser.pol?.interval_days ? `Actual: ${editUser.pol.interval_days}` : "Ej: 90"}
              value={editUser.pol_interval_days_input ?? ""}
              onChange={(ev) => setEditUser({ ...editUser, pol_interval_days_input: ev.target.value })}
              data-testid="admin-edit-pol-interval"
            />
            <p className="text-[11px] text-faded mb-6">
              {editUser.pol?.interval_days
                ? `Actual: cada ${editUser.pol.interval_days} días · próximo: ${editUser.pol.next_checkin ? new Date(editUser.pol.next_checkin).toLocaleDateString() : "—"}`
                : "Sin Fe de Vida configurada todavía"}
            </p>

            <div className="flex gap-3">
              <button onClick={() => setEditUser(null)} className="btn-ghost flex-1 justify-center text-sm">
                Cancelar
              </button>
              <button onClick={saveUserEdit} className="btn-gold flex-1 justify-center text-sm" data-testid="admin-edit-save">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual email modal */}
      {emailUser && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setEmailUser(null)}
        >
          <div
            className="glass-card rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-email-modal"
          >
            <h3 className="font-display text-2xl text-gold-light mb-1">Enviar email manual</h3>
            <p className="text-xs text-faded mb-4">
              Desde <code>support@keepsoul.app</code> a <strong className="text-gold-light">{emailUser.email}</strong> ({emailUser.name})
            </p>

            <label className="font-label text-faded block mb-2">Asunto</label>
            <input
              type="text"
              className="input-field mb-4"
              placeholder="Asunto del email"
              value={emailUser.subject}
              onChange={(ev) => setEmailUser({ ...emailUser, subject: ev.target.value })}
              data-testid="admin-email-subject"
            />

            <label className="font-label text-faded block mb-2">Mensaje</label>
            <textarea
              className="input-field mb-2 min-h-[200px]"
              placeholder="Hola [nombre],&#10;&#10;Tu mensaje aquí..."
              value={emailUser.body}
              onChange={(ev) => setEmailUser({ ...emailUser, body: ev.target.value })}
              data-testid="admin-email-body"
            />
            <p className="text-[11px] text-faded mb-4">
              Usa <code>[nombre]</code> para personalizar con el nombre del usuario. Saltos de línea respetados.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setEmailUser(null)}
                className="btn-ghost flex-1 justify-center text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!emailUser.subject.trim() || !emailUser.body.trim()) {
                    toast.error("Asunto y mensaje son obligatorios");
                    return;
                  }
                  try {
                    const r = await api.post("/admin/email-user", {
                      user_id: emailUser.user_id,
                      subject: emailUser.subject,
                      body: emailUser.body,
                    });
                    if (r.data.sent) {
                      toast.success(`Email enviado a ${r.data.to}`);
                    } else {
                      toast.warning(`Email puesto en cola (Resend no disponible) → ${r.data.to}`);
                    }
                    setEmailUser(null);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Error al enviar");
                  }
                }}
                className="btn-gold flex-1 justify-center text-sm"
                data-testid="admin-email-send"
              >
                <Send className="w-4 h-4" /> Enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
